class CartGamification extends HTMLElement {
  constructor() {
    super();
    this.type = this.dataset.type;
    this.steps = Array.from(this.querySelectorAll(".cart-gamification__step"));
    this.cart =
      document.querySelector("cart-notification") ||
      document.querySelector("cart-drawer");
  }

  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll(".cart-gamification__step"));
    this.init();
  }

  init() {
    this.removeEvents();
    this.bindEvents();
    this.variantsToRemove = this.dataset.variantsToRemove;
    this.productsToRemove = this.dataset.productsToRemove;
    this.variantsToAdd = this.querySelector("[data-variant-to-add]");

    if (this.variantsToRemove || this.productsToRemove) {
      this.removeVariants();
    }

    if (this.variantsToAdd) {
      this.addVariants();
    }
  }

  async removeVariants() {
    // Vérifier s'il y a des variantes ou des produits cadeaux à supprimer
    const variants = (this.variantsToRemove || "").split(",").filter(Boolean);
    const products = (this.productsToRemove || "").split(",").filter(Boolean);
    if (variants.length === 0 && products.length === 0) return;

    try {
      // Récupérer le panier actuel
      const cartResponse = await fetch("/cart.js");
      const cart = await cartResponse.json();

      // Filtrer les items à supprimer
      const itemsToRemove = cart.items
        .filter(
          (item) =>
            item.properties?._type === this.dataset.gift &&
            (variants.includes(item.variant_id.toString()) ||
              products.includes(item.product_id.toString())),
        )
        .map((item) => item.key);

      if (itemsToRemove.length === 0) return;

      // Supprimer les items
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          updates: Object.fromEntries(itemsToRemove.map((key) => [key, 0])),
          sections: "cart-drawer",
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok)
        throw new Error("Erreur lors de la mise à jour du panier");

      const data = await response.json();

      this.closest("cart-drawer").classList.toggle(
        "is-empty",
        data.item_count === 0,
      );

      if (data.sections && data.sections["cart-drawer"]) {
        await refreshCartDrawer(data.sections["cart-drawer"]);
      } else {
        await refreshCartDrawer();
      }
    } catch (error) {
      console.error("Erreur lors de la suppression des variantes:", error);
    }
  }

  async addVariants() {
    // Vérifier s'il y a des variantes à ajouter
    if (!this.variantsToAdd) return;

    const variants = this.variantsToAdd.dataset.variantToAdd
      .split(",")
      .filter(Boolean);
    if (variants.length === 0) return;

    try {
      // Ajouter les cadeaux un par un : une variante en erreur ne bloque pas les autres
      let lastData = null;
      for (const variant of variants) {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            items: [
              {
                id: variant,
                quantity: 1,
                properties: { _type: this.dataset.gift },
              },
            ],
            sections: "cart-drawer",
            sections_url: window.location.pathname,
          }),
        });

        if (!response.ok) {
          console.error("Cadeau non ajouté (variante " + variant + ")");
          continue;
        }
        lastData = await response.json();
      }

      if (!lastData) throw new Error("Erreur lors de l'ajout des variantes");

      if (lastData.sections && lastData.sections["cart-drawer"]) {
        await refreshCartDrawer(lastData.sections["cart-drawer"]);
      } else {
        await refreshCartDrawer();
      }

      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        cartDrawer.open();
      }
    } catch (error) {
      console.error("Erreur lors de l'ajout des variantes:", error);
    }
  }

  removeEvents() {
    this.querySelectorAll("[data-add-to-cart]").forEach((button) => {
      button.removeEventListener("click", this.handleAddToCart.bind(this));
    });

    this.querySelectorAll(".cart-gamification__variant-option input").forEach(
      (input) => {
        input.removeEventListener(
          "change",
          this.handleVariantChange.bind(this),
        );
      },
    );

    document.removeEventListener("click", (event) => {
      if (!event.target.closest("cart-gamification details")) {
        this.querySelectorAll("details[open]").forEach((details) => {
          details.removeAttribute("open");
        });
      }
    });
  }

  bindEvents() {
    this.querySelectorAll("[data-add-to-cart]").forEach((button) => {
      button.addEventListener("click", this.handleAddToCart.bind(this));
    });

    this.querySelectorAll(".cart-gamification__variant-option input").forEach(
      (input) => {
        input.addEventListener("change", this.handleVariantChange.bind(this));
      },
    );

    document.addEventListener("click", (event) => {
      if (!event.target.closest("cart-gamification details")) {
        this.querySelectorAll("details[open]").forEach((details) => {
          details.removeAttribute("open");
        });
      }
    });
  }

  handleVariantChange(event) {
    const input = event.currentTarget;
    const label = input.closest("label");
    const productContainer = input.closest("[data-product-id]");
    const addToCartButton =
      productContainer.querySelector("[data-add-to-cart]");
    const variantId = input.value;
    const summary = productContainer.querySelector(
      "summary .cart-gamification__variant-price",
    );
    const allLabels = productContainer.querySelectorAll(
      ".cart-gamification__variant-option",
    );

    // Retirer la classe active de tous les labels
    allLabels.forEach((l) => l.classList.remove("option-selected"));

    // Ajouter la classe active au label sélectionné
    label.classList.add("option-selected");

    // Mettre à jour le summary
    if (summary) {
      summary.textContent = label.textContent;
    }

    // Mettre à jour le variant ID
    if (addToCartButton) {
      addToCartButton.dataset.variantId = variantId;
      addToCartButton.removeAttribute("disabled");
    }

    input.closest("details").removeAttribute("open");
  }

  initSwipers() {
    this.querySelectorAll("[data-products-slider]").forEach((slider) => {
      if (slider.swiper) return;

      new Swiper(slider, {
        slidesPerView: "auto",
        spaceBetween: 16,
        grabCursor: true,
        navigation: {
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        },
        breakpoints: {
          768: {
            slidesPerView: "auto",
            spaceBetween: 24,
          },
        },
      });
    });
  }

  async handleAddToCart(event) {
    const button = event.currentTarget;
    button.classList.add("loading");
    button.querySelector(".loading__spinner").classList.remove("hidden");
    const productContainer = button.closest("[data-product-id]");
    const variantSelector = productContainer.querySelector(
      "[data-variant-selector]",
    );
    const variantId =
      button.dataset.variantId ||
      (variantSelector ? variantSelector.value : null);

    if (!variantId) return;

    button.setAttribute("disabled", true);

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: { _type: this.dataset.gift },
            },
          ],
          sections: "cart-drawer",
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) throw new Error("Erreur lors de l'ajout au panier");

      const data = await response.json();

      if (data.sections && data.sections["cart-drawer"]) {
        await refreshCartDrawer(data.sections["cart-drawer"]);
      } else {
        await refreshCartDrawer();
      }

      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        cartDrawer.open();
      }
    } catch (error) {
      console.error("Erreur:", error);
    } finally {
      button.removeAttribute("disabled");
      button.classList.remove("loading");
      button.querySelector(".loading__spinner").classList.add("hidden");
    }
  }
}

customElements.define("cart-gamification", CartGamification);

/*
  Choix de la taille d'un cadeau directement sur sa ligne du panier.
  Remplace la variante cadeau par celle choisie (retrait puis ajout),
  en conservant la propriété _type. Si l'ajout échoue, l'ancienne taille est remise.
*/
if (!window.__cartGiftSizeBound) {
  window.__cartGiftSizeBound = true;

  document.addEventListener("click", async (event) => {
    const option = event.target.closest("[data-gift-size-variant]");
    if (!option || option.getAttribute("aria-checked") === "true") return;

    const group = option.closest("[data-gift-size]");
    if (!group || group.dataset.busy === "true") return;

    const itemKey = group.dataset.itemKey;
    const currentVariant = group.dataset.currentVariant;
    const newVariant = option.dataset.giftSizeVariant;
    const giftType = group.dataset.giftType;

    const addVariant = (id) =>
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{ id, quantity: 1, properties: { _type: giftType } }],
          sections: "cart-drawer",
          sections_url: window.location.pathname,
        }),
      });

    group.dataset.busy = "true";
    group.setAttribute("aria-busy", "true");

    try {
      const removeResponse = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: itemKey, quantity: 0 }),
      });
      if (!removeResponse.ok) throw new Error("Retrait de la taille impossible");

      let addResponse = await addVariant(newVariant);
      if (!addResponse.ok) {
        // Retour à la taille précédente pour ne jamais perdre le cadeau
        addResponse = await addVariant(currentVariant);
        if (!addResponse.ok) throw new Error("Ajout du cadeau impossible");
      }

      const data = await addResponse.json();
      if (data.sections && data.sections["cart-drawer"]) {
        await refreshCartDrawer(data.sections["cart-drawer"]);
      } else {
        await refreshCartDrawer();
      }
    } catch (error) {
      console.error("Erreur lors du changement de taille:", error);
      await refreshCartDrawer();
    }
  });
}
